import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuthStore } from "@/stores/authStore";
import { useToast } from "@/hooks/use-toast";
import api, { getApiError } from "@/services/api";

interface LoggedInJoinPanelProps {
  companyCode: string;
}

type JoinStatus =
  | "loading"
  | "invalid"
  | "member"
  | "pending"
  | "available"
  | "submitting"
  | "done";

/**
 * 로그인한 사용자가 초대 URL(/register?company=코드, QR)로 들어왔을 때 표시되는 패널.
 * 별도 메뉴 없이, 초대 링크 접근 시점에 '현재 계정의 가입여부'를 확인하고
 * 미가입이면 현재 계정 그대로 가입 신청(POST /auth/join-company-request)한다.
 */
export default function LoggedInJoinPanel({ companyCode }: LoggedInJoinPanelProps) {
  const { user, logout } = useAuthStore();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [status, setStatus] = useState<JoinStatus>("loading");
  const [companyName, setCompanyName] = useState<string | null>(null);
  const [companyLogo, setCompanyLogo] = useState<string | null>(null);
  const [doneMessage, setDoneMessage] = useState<string | null>(null);

  // 회사 정보 + 현재 계정의 가입여부 확인
  useEffect(() => {
    let cancelled = false;
    setStatus("loading");
    (async () => {
      try {
        const info = await api.get("/auth/company-info", {
          params: { company_code: companyCode },
        });
        if (cancelled) return;
        setCompanyName(info.data.name);
        setCompanyLogo(info.data.logo_url);

        const chk = await api.get("/auth/check-email", {
          params: { email: user?.email, company_code: companyCode },
        });
        if (cancelled) return;
        if (chk.data.available) {
          setStatus("available");
        } else {
          const reason: string = chk.data.reason || "";
          setStatus(reason.includes("대기") ? "pending" : "member");
        }
      } catch {
        if (!cancelled) setStatus("invalid");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [companyCode, user?.email]);

  const handleJoin = async () => {
    setStatus("submitting");
    try {
      const res = await api.post("/auth/join-company-request", {
        company_code: companyCode,
      });
      setDoneMessage(res.data?.message || null);
      setStatus("done");
    } catch (error) {
      const apiError = getApiError(error);
      if (apiError.code === "ALREADY_MEMBER") {
        setStatus("member");
      } else if (apiError.code === "EMAIL_EXISTS") {
        setStatus("pending");
      } else {
        toast({
          title: "가입 신청 실패",
          description: apiError.message || "잠시 후 다시 시도해주세요.",
          variant: "destructive",
        });
        setStatus("available");
      }
    }
  };

  const handleUseAnotherAccount = async () => {
    await logout();
    navigate(`/register?company=${encodeURIComponent(companyCode)}`, { replace: true });
  };

  const CompanyBadge = () =>
    companyName ? (
      <div className="mx-auto mb-6 inline-flex items-center gap-3 rounded-lg border bg-muted p-4">
        {companyLogo ? (
          <img
            src={companyLogo}
            alt={companyName}
            className="h-10 w-10 rounded object-contain bg-white p-1"
          />
        ) : (
          <Building2 className="h-10 w-10 rounded bg-white p-2 text-primary" />
        )}
        <p className="text-lg font-semibold">{companyName}</p>
      </div>
    ) : null;

  if (status === "loading") {
    return (
      <div className="mx-auto max-w-md py-16 text-center text-muted-foreground">
        <Loader2 className="mx-auto mb-3 h-6 w-6 animate-spin" />
        회사 정보를 확인하고 있습니다...
      </div>
    );
  }

  if (status === "invalid") {
    return (
      <div className="mx-auto max-w-md text-center">
        <AlertCircle className="mx-auto mb-4 h-12 w-12 text-destructive" />
        <h2 className="mb-2 text-xl font-bold">유효하지 않은 초대 링크</h2>
        <p className="mb-6 text-sm text-muted-foreground">
          회사로부터 받은 링크(또는 QR)를 다시 확인해주세요.
        </p>
        <Button onClick={() => navigate("/")} className="w-full">
          앱으로 이동
        </Button>
      </div>
    );
  }

  if (status === "member") {
    return (
      <div className="mx-auto max-w-md text-center">
        <CompanyBadge />
        <h2 className="mb-2 text-xl font-bold">이미 소속된 회사입니다</h2>
        <p className="mb-6 text-sm text-muted-foreground">
          현재 계정({user?.email})은 이미 이 회사에 소속되어 있습니다. 우측 상단 메뉴의
          '기업 전환'에서 이동할 수 있습니다.
        </p>
        <Button onClick={() => navigate("/")} className="w-full">
          앱으로 이동
        </Button>
      </div>
    );
  }

  if (status === "pending") {
    return (
      <div className="mx-auto max-w-md text-center">
        <CompanyBadge />
        <h2 className="mb-2 text-xl font-bold">이미 가입 신청하셨습니다</h2>
        <p className="mb-6 text-sm text-muted-foreground">
          현재 계정({user?.email})으로 가입 신청이 접수되어 관리자 승인을 기다리고 있습니다.
        </p>
        <Button onClick={() => navigate("/")} className="w-full">
          앱으로 이동
        </Button>
      </div>
    );
  }

  if (status === "done") {
    return (
      <div className="mx-auto max-w-md text-center">
        <CheckCircle2 className="mx-auto mb-4 h-12 w-12 text-green-600" />
        <h2 className="mb-2 text-xl font-bold">가입 신청이 접수되었습니다</h2>
        <CompanyBadge />
        <p className="mb-6 text-sm text-muted-foreground">
          {doneMessage ||
            "관리자 승인 후 우측 상단 '기업 전환'에서 이 회사로 이동할 수 있습니다."}
        </p>
        <Button onClick={() => navigate("/")} className="w-full">
          앱으로 이동
        </Button>
      </div>
    );
  }

  // available / submitting
  return (
    <div className="mx-auto max-w-md text-center">
      <CompanyBadge />
      <h2 className="mb-2 text-xl font-bold">이 회사에 가입 신청하시겠어요?</h2>
      <p className="mb-6 text-sm text-muted-foreground">
        현재 로그인된 <strong>{user?.email}</strong> 계정으로 신청됩니다. 별도 회원가입 없이
        같은 계정(같은 비밀번호)으로 여러 회사를 이용할 수 있습니다.
      </p>
      <Button
        onClick={handleJoin}
        className="w-full"
        disabled={status === "submitting"}
      >
        {status === "submitting" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        {companyName ? `${companyName}에 가입 신청` : "가입 신청"}
      </Button>
      <button
        type="button"
        onClick={handleUseAnotherAccount}
        disabled={status === "submitting"}
        className="mt-4 text-sm text-muted-foreground hover:text-foreground hover:underline disabled:opacity-50"
      >
        다른 계정으로 가입하려면 로그아웃
      </button>
    </div>
  );
}
